import {Epic} from 'redux-observable'
import {concat, from, of} from 'rxjs'
import {
  catchError,
  exhaustMap,
  filter,
  ignoreElements,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators'

import {RootState, AppActions} from 'app/rootReducer'
import Toast from 'components/Basic/Toast'
import {CloudAppResponseType, Policy} from 'interfaces'
import {i18n} from 'i18n'
import {PoliciesService} from 'services/api'
import Logger from 'services/Logger'
import StorageService, {
  POLICIES_STORAGE_NAMESPACE,
} from 'services/StorageService'
import {policiesActions} from '../policiesSlice'

const {t} = i18n

const CLOUD_APP_CACHE_KEY = 'getCloudApp'

const getCloudAppCacheKey = (policyName: string) =>
  `${CLOUD_APP_CACHE_KEY}-${encodeURIComponent(policyName)}`

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** Rejects old cache contracts instead of migrating them into active state. */
const isCurrentCloudAppCache = (
  cached: unknown,
): cached is CloudAppResponseType => {
  if (
    !isRecord(cached) ||
    typeof cached.inheritsCAC !== 'boolean' ||
    !isRecord(cached.cac)
  ) {
    return false
  }

  return Object.entries(cached.cac).every(([appId, value]) => {
    if (!isRecord(value) || !isRecord(value.assignments)) return false
    if ('values' in value || 'consumer_info' in value) return false

    const controls = value.additional_controls
    if (controls !== undefined && !isRecord(controls)) return false
    if (
      controls &&
      ('gemini_allowed' in controls || 'copilot_allowed' in controls)
    ) {
      return false
    }
    if (
      (appId === 'google' || appId === 'o365') &&
      (!controls || typeof controls.consumer_ai_allowed !== 'boolean')
    ) {
      return false
    }
    return true
  })
}

const logStorageError = (message: string, error: unknown) => {
  Logger.LogError(error as Error)
  Logger.LogError(
    new Error(`${message}: ${error instanceof Error ? error.message : error}`),
  )
}

const getCachedCloudApp = async (
  policyName: string,
): Promise<CloudAppResponseType | null> => {
  try {
    const cached = await StorageService.get<unknown>(
      getCloudAppCacheKey(policyName),
      POLICIES_STORAGE_NAMESPACE,
    )
    if (!cached) return null
    if (!isCurrentCloudAppCache(cached)) {
      await removeCloudAppCache(policyName)
      return null
    }

    return cached
  } catch (error) {
    logStorageError('Error retrieving Cloud App cache', error)
    return null
  }
}

const saveCloudAppCache = async (
  policyName: string,
  data: CloudAppResponseType,
): Promise<void> => {
  try {
    await StorageService.set(
      getCloudAppCacheKey(policyName),
      data,
      POLICIES_STORAGE_NAMESPACE,
    )
  } catch (error) {
    logStorageError('Error saving Cloud App cache', error)
  }
}

const removeCloudAppCache = async (policyName: string): Promise<void> => {
  try {
    await StorageService.remove(
      getCloudAppCacheKey(policyName),
      POLICIES_STORAGE_NAMESPACE,
    )
  } catch (error) {
    logStorageError('Error removing Cloud App cache', error)
  }
}

export const getCloudAppEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getCloudApp.match),
    switchMap(({payload}) =>
      from(getCachedCloudApp(payload.policyName)).pipe(
        switchMap(cachedData =>
          concat(
            of(
              ...(cachedData
                ? [policiesActions.getCloudAppSuccess(cachedData)]
                : []),
            ),
            from(PoliciesService.getCloudApp(payload)).pipe(
              mergeMap(({data}) =>
                from(saveCloudAppCache(payload.policyName, data)).pipe(
                  mergeMap(() => [policiesActions.getCloudAppSuccess(data)]),
                ),
              ),
              catchError((message: string) => {
                if (cachedData) {
                  return []
                }

                return concat(of(policiesActions.getCloudAppFailure(message)))
              }),
            ),
          ),
        ),
      ),
    ),
  )

const getCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getCloudAppFailure.match),
    tap(({payload: message}) => {
      Toast({message, type: 'error'})
    }),
    ignoreElements(),
  )

const getBaseCloudAppEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(policiesActions.getBasePolicyCloudApp.match),
    switchMap(() =>
      from(PoliciesService.getCloudApp({policyName: Policy.BasePolicy})).pipe(
        mergeMap(({data}) => [
          policiesActions.getBasePolicyCloudAppSuccess(data),
        ]),
        catchError((message: string) =>
          concat(of(policiesActions.getBasePolicyCloudAppFailure(message))),
        ),
      ),
    ),
  )

const getBaseCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.getBasePolicyCloudAppFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

export const updateCloudAppEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(policiesActions.updateCloudApp.match),
    exhaustMap(({payload}) => {
      const {request, saved} = payload

      return from(PoliciesService.updateCloudApp(request)).pipe(
        mergeMap(() =>
          from(
            request.cac === 'base'
              ? removeCloudAppCache(request.policyName)
              : saveCloudAppCache(request.policyName, saved),
          ).pipe(
            mergeMap(() => [
              policiesActions.updateCloudAppSuccess({
                message: t('policies:CLOUD_APP_UPDATED_SUCCESSFULLY'),
                policyName: request.policyName,
                saved,
              }),
            ]),
          ),
        ),
        catchError((message: string) =>
          concat(
            of(
              policiesActions.updateCloudAppFailure({
                message,
                policyName: request.policyName,
              }),
            ),
          ),
        ),
      )
    }),
  )

export const updateCloudAppFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(policiesActions.updateCloudAppFailure.match),
    tap(({payload: {message, policyName}}) => {
      const isSelectedPolicy =
        policyName === state$.value.policies.selectedPolicyName
      Toast({
        type: 'error',
        message: isSelectedPolicy
          ? message
          : t('policies:CLOUD_APP_SAVE_FAILED_FOR_POLICY', {
              policyName,
              message,
            }),
      })
    }),
    ignoreElements(),
  )

export const updateCloudAppSuccessEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(policiesActions.updateCloudAppSuccess.match),
    filter(
      ({payload}) =>
        payload.policyName === state$.value.policies.selectedPolicyName,
    ),
    tap(({payload: {message}}) => Toast({message, type: 'success'})),
    ignoreElements(),
  )

export const cloudAppEpics = [
  getCloudAppEpic,
  getCloudAppFailureEpic,
  updateCloudAppEpic,
  updateCloudAppFailureEpic,
  updateCloudAppSuccessEpic,
  getBaseCloudAppEpic,
  getBaseCloudAppFailureEpic,
]
